import { familyOfColumn } from "./builder/pickerCatalogue";
import type { FacetValue, RowLabel, ScreenerColumnDef } from "./types";

export const REVENUE_FORECAST_LABEL = "Revenue forecast";

const REVENUE_FORECAST_DESCRIPTION =
  "How much analysts expect sales to grow over the period you choose. Year options compare with the last completed financial year; 2 and 3 years show the average yearly rate. Quarter options compare the coming quarters with the same quarters a year earlier. Fewer analysts cover smaller companies, and fewer still forecast further ahead, so many smaller companies and longer periods have no forecast and won't appear in results.";

/** A horizon of the picker's revenue-forecast family. */
export const isRevenueForecastHorizon = (columnId: string): boolean =>
  familyOfColumn(columnId)?.id === "revenue_forecast";

const METRICS: Record<string, [string, string]> = {
  market_cap_usd: [
    "Market cap",
    "The total value of a company's shares, in US dollars.",
  ],
  revenue_ttm_usd: ["Revenue", "Sales over the past 12 months, in US dollars."],
  revenue_growth_ttm: [
    "Revenue growth",
    "How much sales grew over the past 12 months compared with the previous 12 months.",
  ],
  revenue_cagr_3y: [
    "Revenue growth · 3 years",
    "Average yearly sales growth over three years.",
  ],
  revenue_cagr_5y: [
    "Revenue growth · 5 years",
    "Average yearly sales growth over five years.",
  ],
  eps_growth_ttm: [
    "Earnings growth",
    "Growth in earnings per share over the past 12 months.",
  ],
  eps_growth_fy: [
    "Earnings growth · annual",
    "Earnings-per-share growth in the latest financial year.",
  ],
  revenue_growth_fy1_est: [
    REVENUE_FORECAST_LABEL,
    REVENUE_FORECAST_DESCRIPTION,
  ],
  revenue_cagr_2y_est: [
    "Revenue forecast · 2 years",
    REVENUE_FORECAST_DESCRIPTION,
  ],
  revenue_cagr_3y_est: [
    "Revenue forecast · 3 years",
    REVENUE_FORECAST_DESCRIPTION,
  ],
  revenue_growth_next_2q_est: [
    "Revenue forecast · 2 quarters",
    REVENUE_FORECAST_DESCRIPTION,
  ],
  revenue_growth_next_4q_est: [
    "Revenue forecast · 4 quarters",
    REVENUE_FORECAST_DESCRIPTION,
  ],
  revenue_growth_fy2_est: [
    "Revenue forecast · year 2",
    "Analysts' expected sales growth in the following financial year.",
  ],
  eps_growth_fy1_est: [
    "Earnings forecast",
    "Analysts' expected earnings-per-share growth for the next financial year.",
  ],
  eps_growth_fy2_est: [
    "Earnings forecast · year 2",
    "Analysts' expected earnings-per-share growth in the following financial year.",
  ],
  dividend_yield: [
    "Regular dividend yield",
    "Regular cash dividends from the past 12 months as a percentage of this listing's share price. Special and one-off payments are left out. A record of what was paid, not a forecast.",
  ],
  dividend_yield_ltm: [
    "Dividend yield, last 12 months",
    "Every cash dividend from the past 12 months, including variable and special payments, as a percentage of this listing's share price. A record of what was paid, not a forecast.",
  ],
  dividend_cagr_5y: [
    "Dividend growth · 5 years",
    "Average yearly dividend growth over five years.",
  ],
  fcf_ttm_usd: [
    "Free cash flow",
    "Cash left after running the business and investing in it, in US dollars. Set a minimum of zero for positive cash flow.",
  ],
  fcf_margin_ttm: [
    "Free cash flow margin",
    "The share of revenue left as free cash flow.",
  ],
  fcf_yield: [
    "Free cash flow yield",
    "Free cash flow as a percentage of the company's market value.",
  ],
  fcf_cagr_3y: [
    "Cash flow growth · 3 years",
    "Average yearly free cash flow growth over three years.",
  ],
  ebitda_ttm: [
    "EBITDA",
    "Operating earnings before interest, tax, depreciation and amortisation.",
  ],
  ebitda_margin_ttm: [
    "EBITDA margin",
    "Operating earnings before interest, tax, depreciation and amortisation, as a share of sales.",
  ],
  is_net_cash: [
    "More cash than debt",
    "Companies whose cash and short-term investments exceed their total debt.",
  ],
  shares_change_yoy: [
    "Share dilution",
    "Yearly change in share count. A lower number means less dilution; a negative number means fewer shares.",
  ],
  pe_ttm: [
    "Price / earnings",
    "Share price relative to earnings. A lower ratio means paying less for each dollar of profit.",
  ],
  pe_forward_fy1: [
    "Forward price / earnings",
    "Share price relative to analysts' expected earnings for the next financial year.",
  ],
  pe_forward_fy2: [
    "Forward price / earnings · year 2",
    "Share price relative to expected earnings in the following financial year.",
  ],
  ps_ttm: ["Price / sales", "Company value relative to its annual sales."],
  p_fcf_ttm: [
    "Price / free cash flow",
    "Company value relative to its annual free cash flow.",
  ],
  net_debt_to_ebitda: [
    "Debt / earnings",
    "Debt after deducting cash, relative to EBITDA. Lower values mean less debt relative to earnings.",
  ],
  total_debt: [
    "Total debt",
    "Borrowings on the latest balance sheet, in the company's reporting currency.",
  ],
  cash_and_st_investments: [
    "Cash & investments",
    "Cash and short-term investments in the company's reporting currency.",
  ],
  gross_margin_ttm: [
    "Gross margin",
    "Sales left after the direct cost of producing goods or services.",
  ],
  operating_margin_ttm: [
    "Operating margin",
    "The share of sales left after operating costs.",
  ],
  net_margin_ttm: [
    "Profit margin",
    "The share of sales left as profit after all costs.",
  ],
  beta: [
    "Beta",
    "How strongly the share price tends to move relative to the market.",
  ],
  roic_ttm: [
    "Return on invested capital",
    "How effectively the company turns invested money into operating profit.",
  ],
};

export const metricLabel = (column: ScreenerColumnDef): string => {
  const copy = METRICS[column.id];
  if (copy) return copy[0];
  return column.label
    .replace(/\(TTM, USD\)|\(TTM\)|\(USD\)/g, "")
    .replace(/\(FY1\)/g, "· next year")
    .replace(/\(FY2\)/g, "· year 2")
    .replace(/\(FY\)/g, "· annual")
    .replace(/\(last quarter, YoY\)/g, "· latest quarter")
    .replace(/\(last quarter\)/g, "· latest quarter")
    .replace(/\(YoY\)/g, "· yearly")
    .replace(/CAGR \((\d)y\)/g, "growth · $1 years")
    .replace(/\bFMP\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
};

// The results table's columns are narrow and even: long ratio names take
// their usual short forms there.
const HEADERS: Record<string, string> = {
  pe_ttm: "P/E",
  pe_forward_fy1: "Forward P/E",
  pe_forward_fy2: "Forward P/E, year 2",
  ps_ttm: "P/S",
  p_fcf_ttm: "P/FCF",
  roic_ttm: "ROIC",
  fcf_margin_ttm: "FCF margin",
  fcf_yield: "FCF yield",
  dividend_yield: "Dividend yield",
  dividend_yield_ltm: "Dividend yield 12M",
};

const compactPeriod = (label: string): string =>
  label
    .replace(/ · (\d) years?$/, " $1Y")
    .replace(/ · (\d) quarters?$/, " $1Q");

/**
 * The results table's column headers, with periods as 3Y and 2Q. A revenue
 * forecast is just "Revenue forecast": the user picked its horizon (Ben,
 * 2026-09-28). Two horizons on screen at once keep theirs, so no two headers
 * read the same.
 */
export const columnHeaders = (
  columns: readonly ScreenerColumnDef[],
): string[] => {
  const full = columns.map(
    (column) => HEADERS[column.id] ?? compactPeriod(metricLabel(column)),
  );
  const short = columns.map((column, index) =>
    isRevenueForecastHorizon(column.id) ? REVENUE_FORECAST_LABEL : full[index],
  );
  return short.map((label, index) =>
    short.filter((other) => other === label).length > 1 ? full[index] : label,
  );
};

// Trusts and partnerships (MLPs) pay distributions, not dividends.
const paysDistributions = (labels: readonly RowLabel[]): boolean =>
  labels.includes("trust_units") || labels.includes("partnership_units");

/** The yield row of a trust or a partnership says so. */
export const metricLabelFor = (
  column: ScreenerColumnDef,
  labels: readonly RowLabel[],
): string => {
  if (column.id === "dividend_yield" && paysDistributions(labels))
    return "Distribution yield";
  if (column.id === "dividend_yield_ltm" && paysDistributions(labels))
    return "Distribution yield, last 12 months";
  return metricLabel(column);
};

/** The catalogue label (with its period) for one row, as the popover titles it. */
export const catalogueLabelFor = (
  column: ScreenerColumnDef,
  labels: readonly RowLabel[],
): string => {
  if (column.id === "dividend_yield" && paysDistributions(labels))
    return "Distribution yield (TTM)";
  if (column.id === "dividend_yield_ltm" && paysDistributions(labels))
    return "Distribution yield, last 12 months";
  return column.label;
};

export const metricDescription = (column: ScreenerColumnDef): string => {
  const copy = METRICS[column.id];
  if (copy) return copy[1];
  return column.description
    .replace(/\bFMP(?:'s)?\s*/gi, "")
    .replace(/\bTTM\b/g, "past 12 months")
    .replace(/\bFY1\b/g, "next financial year")
    .replace(/\bFY2\b/g, "following financial year")
    .replace(/\bFY\b/g, "financial year")
    .replace(/[,;] (?:null|clipped|needs|withheld).*$/i, ".");
};

export const presentColumn = (
  column: ScreenerColumnDef,
): ScreenerColumnDef => ({
  ...column,
  label: metricLabel(column),
  description: metricDescription(column),
});

/** The operator a new row opens on, as the row shows it (a negated column's turned round). */
export const defaultDirection = (column: ScreenerColumnDef): "gte" | "lte" => {
  if (
    /^(pe_|ps_|pb$|p_fcf|p_ocf|ev_to_|net_debt_to_|debt_to_|shares_change|shares_cagr|payout_ratio|peg_ratio|pct_from_52w_low$|pct_from_52w_high$)/.test(
      column.id,
    )
  )
    return "lte";
  return "gte";
};

/**
 * Columns where the natural question is the sign, not a threshold: cash
 * flows and profits, margins and returns, growth rates, net debt, share-count
 * change and target upside. The row offers Positive / Negative (as > 0 / < 0,
 * so zero is neither) ahead of the threshold operators; a reporting-currency
 * money column (`isSignOnly`) offers nothing else. The moving-average gaps
 * read as Above / Below 0% through the threshold operators themselves.
 */
const SIGN_ELIGIBLE =
  /^(fcf_ttm|fcf_ttm_usd|ocf_ttm|net_income_ttm|operating_income_ttm|ebitda_ttm|gross_profit_ttm|eps_ttm|eps_estimate_fy[12]|net_cash_per_share|ncav_per_share|tangible_book_value_per_share|net_debt|net_debt_usd|net_debt_to_fcf|net_debt_to_ebitda|(gross|operating|ebitda|net|fcf)_margin_ttm|ro[eai]c?_ttm|earnings_yield|fcf_yield|buyback_yield|(revenue|eps|net_income|ebitda|ocf|fcf|dividend)_growth_[a-z0-9_]+|(revenue|eps|fcf|dividend|shares)_cagr_[a-z0-9_]+|shares_change_[a-z]+|eps_estimate_fy1_revision_3m|ret_[a-z0-9]+|price_target_(upside|trend|low_upside)|eps_surprise_last)$/;

// Never below zero, so a strict Negative could match nothing.
const NEVER_NEGATIVE: ReadonlySet<string> = new Set([
  "net_debt_to_fcf",
  "dividend_growth_streak_years",
  "revenue_growth_ttm_rank",
]);

export const signEligible = (column: ScreenerColumnDef): boolean =>
  column.kind === "number" &&
  SIGN_ELIGIBLE.test(column.id) &&
  !NEVER_NEGATIVE.has(column.id);

// Typed thresholds beyond the sign-eligible columns whose values run below
// zero: the moving-average gaps, beta, Altman Z, Rule of 40, interest cover,
// income quality, gross profit to assets, the cash conversion cycle and the
// enterprise-value measures (negative when cash exceeds the company's value).
const NEGATIVE_THRESHOLDS =
  /^(price_vs_sma_\d+|sma_50_vs_sma_200|beta|altman_z|rule_of_40|interest_coverage|income_quality|gross_profit_to_assets|cash_conversion_cycle|enterprise_value_usd|ev_to_(sales|ebitda)_ttm)$/;

/**
 * A typed bound on the column may be below zero, so a touch row offers a ±
 * key: the iOS decimal keypad has no minus.
 */
export const negativeAllowed = (column: ScreenerColumnDef): boolean =>
  signEligible(column) || NEGATIVE_THRESHOLDS.test(column.id);

/** The two sign labels, [> 0, < 0]. Generic unless "Positive" would mislead. */
export const signLabels = (column: ScreenerColumnDef): [string, string] => {
  if (/^net_debt/.test(column.id)) return ["Net debt", "Net cash"];
  if (/^shares_(change|cagr)/.test(column.id))
    return ["Diluting", "Buying back"];
  if (column.id === "eps_surprise_last") return ["Beat", "Miss"];
  return ["Positive", "Negative"];
};

// The pick-list criteria's vocabularies, in reading order. The served facets
// say which values the data holds; these order them and stand in while the
// facets are unavailable, so the row always has its choices.
const ENUM_ORDER: Record<string, readonly string[]> = {
  consensus_rating: ["Strong Buy", "Buy", "Hold", "Sell", "Strong Sell"],
  dividend_frequency: [
    "Weekly",
    "Monthly",
    "Quarterly",
    "Semi-Annual",
    "Annual",
    "Irregular",
    "Special",
  ],
};

const ENUM_LABELS: Record<string, Record<string, string>> = {
  dividend_frequency: { "Semi-Annual": "Semi-annual", Special: "Special only" },
};

/**
 * A pick-list criterion's choices: the values the data holds in their
 * natural order, then any the vocabulary doesn't name, then any a saved
 * screen selected that the data no longer holds (so it can be removed).
 */
export const enumChoices = (
  columnId: string,
  facet: readonly FacetValue[] = [],
  selected: readonly string[] = [],
): string[] => {
  const order = ENUM_ORDER[columnId] ?? [];
  const served = facet.map((entry) => entry.value);
  const known = order.filter(
    (value) => served.length === 0 || served.includes(value),
  );
  const choices = [
    ...known,
    ...served.filter((value) => !order.includes(value)),
  ];
  return [...choices, ...selected.filter((value) => !choices.includes(value))];
};

export const enumLabel = (columnId: string, value: string): string =>
  ENUM_LABELS[columnId]?.[value] ?? value;
